import type { AgentChatThreadSummary, GraphEntityType, GraphFrameListItem, GraphVisualizationFrame } from "@salesko/contracts";
import { History, MessageSquare, MessageSquarePlus, Network, SlidersHorizontal, Trash2 } from "lucide-react";
import { Switch } from "@salesko/design-system";
import { useState } from "react";
import type { CSSProperties } from "react";
import { entityMeta } from "../entityMeta";
import { entityLabel } from "../i18n";
import { compactDateTime } from "../lib/format";
import { stableGraphFrameSummaryTitle } from "../lib/stableText";
import { useI18n } from "../../i18n";

const entityOrder: GraphEntityType[] = ["person", "organisation", "event", "opportunity"];
type RailTab = "graph" | "conversations";

type LeftRailProps = {
  activeFrameRecordId: string | null;
  activeThreadId: string | null;
  filters: Record<GraphEntityType, boolean>;
  frame: GraphVisualizationFrame;
  frameItems: GraphFrameListItem[];
  frameListError: boolean;
  frameListPending: boolean;
  onDeleteThread: (threadId: string) => void;
  onNewThread: () => void;
  onSelectFrameRecord: (frameRecordId: string) => void;
  onSelectThread: (threadId: string) => void;
  onToggleFilter: (entityType: GraphEntityType) => void;
  restoreErrorFrameId: string | null;
  showFrameHistory: boolean;
  showThreadHistory: boolean;
  threadItems: AgentChatThreadSummary[];
  threadListError: boolean;
  threadListPending: boolean;
  threadRestoreErrorId: string | null;
};

export function LeftRail({
  activeFrameRecordId,
  activeThreadId,
  filters,
  frame,
  frameItems,
  frameListError,
  frameListPending,
  onDeleteThread,
  onNewThread,
  onSelectFrameRecord,
  onSelectThread,
  onToggleFilter,
  restoreErrorFrameId,
  showFrameHistory,
  showThreadHistory,
  threadItems,
  threadListError,
  threadListPending,
  threadRestoreErrorId
}: LeftRailProps) {
  const { t } = useI18n();
  const [activeRailTab, setActiveRailTab] = useState<RailTab>("graph");
  const relationshipStrength = Math.round((frame.ui.relationshipStrength ?? 0.58) * 100);
  const stats = frame.ui.stats ?? {
    events: 0,
    opportunities: 0,
    organisations: 0,
    people: 0
  };
  const countByType: Record<GraphEntityType, number> = {
    person: stats.people,
    organisation: stats.organisations,
    event: stats.events,
    opportunity: stats.opportunities,
    note: 0
  };
  const entityRows = entityOrder.map((entityType) => ({
    count: countByType[entityType],
    enabled: filters[entityType] !== false,
    entityType,
    label: entityLabel(t, entityType),
    meta: entityMeta[entityType]
  }));
  const visibleRailTab: RailTab = showThreadHistory && activeRailTab === "conversations" ? "conversations" : "graph";
  const railTabsLabel = showThreadHistory ? `${t.nav.graph} / ${t.leftRail.recentConversations}` : t.nav.graph;

  return (
    <aside className="workspace-rail" aria-label={t.leftRail.entitiesLabel}>
      <div className="rail-card-header">
        <div aria-label={railTabsLabel} className="rail-card-tabs" role="tablist">
          <button
            aria-controls="left-rail-panel-graph"
            aria-selected={visibleRailTab === "graph"}
            className={visibleRailTab === "graph" ? "rail-card-tab is-active" : "rail-card-tab"}
            id="left-rail-tab-graph"
            onClick={() => setActiveRailTab("graph")}
            role="tab"
            type="button"
          >
            <Network size={14} />
            <span>{t.nav.graph}</span>
          </button>
          {showThreadHistory ? (
            <button
              aria-controls="left-rail-panel-conversations"
              aria-selected={visibleRailTab === "conversations"}
              className={visibleRailTab === "conversations" ? "rail-card-tab is-active" : "rail-card-tab"}
              id="left-rail-tab-conversations"
              onClick={() => setActiveRailTab("conversations")}
              role="tab"
              type="button"
            >
              <MessageSquare size={14} />
              <span>{t.leftRail.recentConversations}</span>
            </button>
          ) : null}
        </div>
        {visibleRailTab === "conversations" ? (
          <button
            aria-label={t.leftRail.newConversationAriaLabel}
            className="history-refresh rail-card-action"
            onClick={onNewThread}
            title={t.leftRail.newConversationTitle}
            type="button"
          >
            <MessageSquarePlus size={14} />
          </button>
        ) : null}
      </div>

      {visibleRailTab === "graph" ? (
        <div aria-labelledby="left-rail-tab-graph" className="rail-card-panel" id="left-rail-panel-graph" role="tabpanel">
          <section className="rail-section" aria-label={t.leftRail.entitiesLabel}>
            <div className="section-label">
              <span>{t.leftRail.entitiesLabel}</span>
              <SlidersHorizontal size={15} />
            </div>
            <div className="entity-list">
              {entityRows.map((row) => (
                <div className="entity-row" key={row.entityType}>
                  <span className={`entity-dot entity-dot-${row.meta.tone}`} aria-hidden="true" />
                  <span className="entity-row-label">{row.label}</span>
                  <span className="entity-row-count">{row.count.toLocaleString()}</span>
                  <Switch
                    ariaLabel={t.leftRail.toggleEntity(row.label)}
                    checked={row.enabled}
                    onChange={() => onToggleFilter(row.entityType)}
                    size="sm"
                    tone={row.meta.tone as "person" | "company" | "event" | "opportunity"}
                  />
                </div>
              ))}
            </div>
          </section>

          <section className="rail-section" aria-label={t.leftRail.relationshipStrengthLabel}>
            <div
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuenow={relationshipStrength}
              className="strength-control"
              role="meter"
              style={{ "--strength": `${relationshipStrength}%` } as CSSProperties}
            >
              <div className="section-label section-label-tight">
                <span>{t.leftRail.relationshipStrengthLabel}</span>
                <strong>{relationshipStrength}%</strong>
              </div>
              <div className="strength-track">
                <span />
              </div>
              <div className="strength-labels">
                <span>{t.leftRail.strengthWeak}</span>
                <span>{t.leftRail.strengthMedium}</span>
                <span>{t.leftRail.strengthStrong}</span>
              </div>
            </div>
          </section>

          {showFrameHistory ? (
            <section className="rail-section" aria-label={t.leftRail.recentFrames}>
              <div className="section-label">
                <span>{t.leftRail.recentFrames}</span>
                <History size={15} />
              </div>
              {frameListPending ? (
                <p className="frame-history-hint">{t.leftRail.loadingFrames}</p>
              ) : frameListError ? (
                <p className="frame-history-hint">{t.leftRail.frameHistoryUnavailable}</p>
              ) : frameItems.length === 0 ? (
                <p className="frame-history-hint">{t.leftRail.noFramesYet}</p>
              ) : (
                <ul className="frame-history">
                  {frameItems.map((item) => {
                    const active = item.id === activeFrameRecordId;
                    return (
                      <li key={item.id}>
                        <button
                          aria-current={active ? "true" : undefined}
                          className={active ? "frame-history-item is-active" : "frame-history-item"}
                          onClick={() => onSelectFrameRecord(item.id)}
                          type="button"
                        >
                          <span className="frame-history-title">{frameHistoryTitle(item)}</span>
                          <span className="frame-history-meta">
                            {t.leftRail.frameNodeCount(item.nodeCount)} · {compactDateTime(item.acceptedAt)}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {restoreErrorFrameId ? (
                <p className="frame-history-hint frame-history-error" role="alert">
                  {t.leftRail.frameLoadError(restoreErrorFrameId)}
                </p>
              ) : null}
            </section>
          ) : null}
        </div>
      ) : null}

      {visibleRailTab === "conversations" && showThreadHistory ? (
        <div
          aria-labelledby="left-rail-tab-conversations"
          className="rail-card-panel"
          id="left-rail-panel-conversations"
          role="tabpanel"
        >
          {threadListPending ? (
            <p className="frame-history-hint">{t.leftRail.loadingConversations}</p>
          ) : threadListError ? (
            <p className="frame-history-hint">{t.leftRail.conversationHistoryUnavailable}</p>
          ) : threadItems.length === 0 ? (
            <p className="frame-history-hint">{t.leftRail.noConversationsYet}</p>
          ) : (
            <ul className="frame-history">
              {threadItems.map((item) => {
                const active = item.threadId === activeThreadId;
                return (
                  <li className="thread-history-row" key={item.threadId}>
                    <button
                      aria-current={active ? "true" : undefined}
                      className={active ? "frame-history-item is-active" : "frame-history-item"}
                      onClick={() => onSelectThread(item.threadId)}
                      type="button"
                    >
                      <span className="frame-history-title">{threadHistoryTitle(item)}</span>
                      <span className="frame-history-meta">
                        {t.leftRail.threadMessageCount(item.messageCount)} · {compactDateTime(item.updatedAt)}
                      </span>
                    </button>
                    <button
                      aria-label={t.leftRail.deleteConversationAriaLabel(threadHistoryTitle(item))}
                      className="history-refresh relationship-delete-button"
                      onClick={() => onDeleteThread(item.threadId)}
                      title={t.leftRail.deleteConversationTitle}
                      type="button"
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {threadRestoreErrorId ? (
            <p className="frame-history-hint frame-history-error" role="alert">
              {t.leftRail.conversationLoadError(threadRestoreErrorId)}
            </p>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
}

function frameHistoryTitle(item: GraphFrameListItem) {
  const summary = stableGraphFrameSummaryTitle(item.summary);
  if (summary.length === 0) {
    return item.id;
  }

  return summary.length > 64 ? `${summary.slice(0, 61)}…` : summary;
}

function threadHistoryTitle(item: AgentChatThreadSummary) {
  const title = item.title.trim();
  if (title.length === 0) {
    return item.threadId;
  }

  return title.length > 64 ? `${title.slice(0, 61)}…` : title;
}
